import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Stack,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api';
import { useNavigate } from 'react-router-dom';
import PasswordValidator from './PasswordValidator';

const TemporaryPasswordResetForm = () => {
  const [formData, setFormData] = useState({
    newPassword: '',
    newPasswordConfirm: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, strength: 0 });
  
  const { user, setAuthAfterReset } = useAuth();
  const navigate = useNavigate();
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else if (!passwordValidation.isValid) {
      newErrors.newPassword = 'Password does not meet security requirements';
    }
    
    if (!formData.newPasswordConfirm.trim()) {
      newErrors.newPasswordConfirm = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.newPasswordConfirm) {
      newErrors.newPasswordConfirm = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post('/auth/reset-temporary-password', {
        newPassword: formData.newPassword,
        newPasswordConfirm: formData.newPasswordConfirm
      });
      
      const loggedInUser = response.data?.data?.user;
      if (response.data.success && loggedInUser) {
        setAuthAfterReset(loggedInUser);
      }
      
      setSuccess(true);
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('[TemporaryPasswordReset] Error:', error);
      
      if (error.response?.data?.message) {
        setApiError(error.response.data.message);
      } else {
        setApiError('An error occurred while resetting your password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };
  
  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
          Set Your Password
        </Typography>
        
        <Typography paragraph variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please set a new password to continue.
        </Typography>
        
        {apiError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {apiError}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Password updated successfully! Redirecting...
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="New Password"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleChange}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              disabled={loading || success}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('new')}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {formData.newPassword && (
              <PasswordValidator 
                password={formData.newPassword}
                onValidationChange={setPasswordValidation}
              />
            )}
            
            <TextField
              fullWidth
              label="Confirm New Password"
              name="newPasswordConfirm"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.newPasswordConfirm}
              onChange={handleChange}
              error={!!errors.newPasswordConfirm}
              helperText={errors.newPasswordConfirm}
              disabled={loading || success}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('confirm')}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={loading || success}
            >
              {loading ? 'Processing...' : 'Reset Password'}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default TemporaryPasswordResetForm; 